import { ReactNode, useCallback, useMemo } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import {
    ORGANIZATION_INTEGRATION_STATUS_LABELS,
    ORGANIZATION_INTEGRATION_STATUS_ORDER,
    OrganizationIntegrationStatus,
} from "@common/helpers/organizationIntegrationStatus";
import {
    FilterableConnectionProvider,
    filterableConnectionProviderSchema,
    UnifiedConnectionProvider,
} from "@common/schemas/organization-unified-connection.schema";
import { BankIcon, StatusIcon, TriangleIcon } from "@icons";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import statusStyles from "@components/pageHeader/filters/Tabs/TransactionStatusFilter/TransactionStatusFilter.module.scss";

const STATUS_TAB = "status";
const PROVIDER_TAB = "provider";

type IntegrationFilterTab = typeof STATUS_TAB | typeof PROVIDER_TAB;

const PROVIDER_LABELS: Record<UnifiedConnectionProvider, string> = {
    plaid: "Bank (Plaid)",
    paypal: "PayPal",
    mercury: "Mercury",
    quickbooks: "QuickBooks",
    billcom: "Bill.com",
    gusto: "Gusto",
    adp_run: "ADP RUN",
    stripe: "Stripe",
    ramp: "Ramp",
};

// Labels/ranks cover every unified provider (not just the filterable ones), so
// a provider added to `unifiedConnectionProviderSchema` can't be silently
// missing: it has no rank here and no label above, and both records fail to
// compile.
const PROVIDER_RANK: Record<UnifiedConnectionProvider, number> = {
    plaid: 0,
    paypal: 1,
    mercury: 2,
    quickbooks: 3,
    billcom: 4,
    gusto: 5,
    stripe: 6,
    ramp: 7,
    adp_run: 8,
};

// Only filterable (transaction-feed) providers are offered: QuickBooks never
// appears as a connection in this view, so filtering by it would match nothing.
const PROVIDER_ORDER: FilterableConnectionProvider[] = [
    ...filterableConnectionProviderSchema.options,
].sort((a, b) => PROVIDER_RANK[a] - PROVIDER_RANK[b]);

const STATUS_OPTIONS: FilterMultiselectOption[] =
    ORGANIZATION_INTEGRATION_STATUS_ORDER.map((status) => ({
        value: status,
        label: ORGANIZATION_INTEGRATION_STATUS_LABELS[status],
    }));

const PROVIDER_OPTIONS: FilterMultiselectOption[] = PROVIDER_ORDER.map(
    (provider) => ({
        value: provider,
        label: PROVIDER_LABELS[provider],
    }),
);

const STATUS_VALUES: string[] = ORGANIZATION_INTEGRATION_STATUS_ORDER;
const PROVIDER_VALUES: string[] = PROVIDER_ORDER;

function isStatus(
    value: FilterMultiselectValue,
): value is OrganizationIntegrationStatus {
    return typeof value === "string" && STATUS_VALUES.includes(value);
}

function isProvider(
    value: FilterMultiselectValue,
): value is FilterableConnectionProvider {
    return typeof value === "string" && PROVIDER_VALUES.includes(value);
}

const IntegrationFilterNavTab = ({
    tabKey,
    label,
    icon,
}: Readonly<{
    tabKey: IntegrationFilterTab;
    label: string;
    icon: ReactNode;
}>) => (
    <FilterNavTab eventKey={tabKey} className={overlayStyles.navigationButton}>
        <span className={overlayStyles.navigationItem}>
            <span className={overlayStyles.navigationIcon}>{icon}</span>
            {label}
            <TriangleIcon
                className={classNames(
                    overlayStyles.navigationActiveIndicator,
                    "icon-color-gray-500",
                )}
            />
        </span>
    </FilterNavTab>
);

const IntegrationFilterPanel = ({
    tabKey,
    name,
    options,
    value,
    onChange,
}: Readonly<{
    tabKey: IntegrationFilterTab;
    name: string;
    options: FilterMultiselectOption[];
    value: FilterMultiselectValue[];
    onChange: (values: FilterMultiselectValue[]) => void;
}>) => (
    <FilterTabPanel eventKey={tabKey}>
        <div className={statusStyles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={onChange}
                name={name}
                onBlur={noop}
            />
        </div>
    </FilterTabPanel>
);

const IntegrationActiveFilterChip = ({
    tabKey,
    labels,
    pluralNoun,
    onReset,
}: Readonly<{
    tabKey: IntegrationFilterTab;
    labels: string[];
    pluralNoun: string;
    onReset: () => void;
}>) => {
    const { open } = useAggregatedFiltersContext();
    const text = formatFilterLabelText(labels, pluralNoun);

    if (text === null) {
        return null;
    }

    return (
        <ActiveFilterButton
            openFilter={() => open(tabKey)}
            resetFilter={onReset}
            label={text}
        />
    );
};

interface IntegrationFiltersControlsProps {
    statuses: OrganizationIntegrationStatus[];
    providers: FilterableConnectionProvider[];
    onStatusesChange: (statuses: OrganizationIntegrationStatus[]) => void;
    onProvidersChange: (providers: FilterableConnectionProvider[]) => void;
}

/**
 * "Add filter" popover + active-filter chips for the org integrations table.
 * Built on the shared `AggregatedFilters` primitives rather than
 * `TablePageHeader`'s `availableFilters`: that framework's `FilterTabs` enum is
 * accounting-domain specific and has no integration status or provider concept,
 * so using it would mean changing the shared filter schema for one page.
 */
export const IntegrationFiltersControls = ({
    statuses,
    providers,
    onStatusesChange,
    onProvidersChange,
}: IntegrationFiltersControlsProps) => {
    const activeFilters = useMemo<IntegrationFilterTab[]>(() => {
        const active: IntegrationFilterTab[] = [];
        if (statuses.length > 0) {
            active.push(STATUS_TAB);
        }
        if (providers.length > 0) {
            active.push(PROVIDER_TAB);
        }
        return active;
    }, [statuses.length, providers.length]);

    const handleStatusValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onStatusesChange(values.filter(isStatus)),
        [onStatusesChange],
    );

    const handleProviderValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onProvidersChange(values.filter(isProvider)),
        [onProvidersChange],
    );

    const clearFilters = useCallback(() => {
        onStatusesChange([]);
        onProvidersChange([]);
    }, [onStatusesChange, onProvidersChange]);

    const resetStatuses = useCallback(
        () => onStatusesChange([]),
        [onStatusesChange],
    );

    const resetProviders = useCallback(
        () => onProvidersChange([]),
        [onProvidersChange],
    );

    return (
        <AggregatedFiltersProvider defaultTab={STATUS_TAB}>
            <AggregatedFilters label="Add filter">
                <AggregatedFiltersOverlay
                    filterClassname={overlayStyles.filter}
                    navigation={
                        <>
                            <IntegrationFilterNavTab
                                tabKey={STATUS_TAB}
                                label="Status"
                                icon={<StatusIcon />}
                            />
                            <IntegrationFilterNavTab
                                tabKey={PROVIDER_TAB}
                                label="Provider"
                                icon={<BankIcon />}
                            />
                        </>
                    }
                    content={
                        <>
                            <IntegrationFilterPanel
                                tabKey={STATUS_TAB}
                                name="Status"
                                options={STATUS_OPTIONS}
                                value={statuses}
                                onChange={handleStatusValuesChange}
                            />
                            <IntegrationFilterPanel
                                tabKey={PROVIDER_TAB}
                                name="Provider"
                                options={PROVIDER_OPTIONS}
                                value={providers}
                                onChange={handleProviderValuesChange}
                            />
                        </>
                    }
                />
            </AggregatedFilters>

            {activeFilters.length > 0 && (
                <ActiveFilters
                    activeFilters={activeFilters}
                    maxVisibleFilters={activeFilters.length}
                    clearFilters={clearFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map((tabKey) =>
                            tabKey === STATUS_TAB ? (
                                <IntegrationActiveFilterChip
                                    key={tabKey}
                                    tabKey={STATUS_TAB}
                                    pluralNoun="Statuses"
                                    labels={statuses.map(
                                        (status) =>
                                            ORGANIZATION_INTEGRATION_STATUS_LABELS[
                                                status
                                            ],
                                    )}
                                    onReset={resetStatuses}
                                />
                            ) : (
                                <IntegrationActiveFilterChip
                                    key={tabKey}
                                    tabKey={PROVIDER_TAB}
                                    pluralNoun="Providers"
                                    labels={providers.map(
                                        (provider) => PROVIDER_LABELS[provider],
                                    )}
                                    onReset={resetProviders}
                                />
                            ),
                        )
                    }
                />
            )}
        </AggregatedFiltersProvider>
    );
};
